import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

/** InlineConfirm - inline confirmation prompt with confirm/cancel actions and loading state. */
interface InlineConfirmProps {
  readonly message: string;
  readonly visible: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly loading?: boolean;
  readonly variant?: 'danger' | 'warning' | 'default';
}

export default function InlineConfirm({
  message, visible, onConfirm, onCancel,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  loading, variant = 'danger',
}: InlineConfirmProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  if (!visible) return null;

  const variantColor = variant === 'danger' ? colors.danger : variant === 'warning' ? colors.warning : colors.primary;

  return (
    <View style={[styles.container, { borderColor: variantColor + '40', backgroundColor: variantColor + '08' }]}>
      <View style={styles.messageRow}>
        <Ionicons
          name={variant === 'danger' ? 'warning' : variant === 'warning' ? 'alert-circle' : 'help-circle'}
          size={18}
          color={variantColor}
        />
        <Text style={[styles.message, { color: colors.text }]}>{message}</Text>
      </View>
      <View style={styles.actions}>
        <TouchableOpacity style={styles.cancelBtn} onPress={onCancel} disabled={loading}>
          <Text style={[styles.cancelText, { color: colors.textSecondary }]}>{cancelLabel}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.confirmBtn, { backgroundColor: variantColor }]}
          onPress={onConfirm}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.confirmText}>{confirmLabel}</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: {
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    padding: spacing.md,
    marginTop: spacing.sm,
    gap: spacing.md,
  },
  messageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  message: {
    flex: 1,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
  },
  cancelBtn: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm,
    backgroundColor: c.surfaceSecondary,
  },
  cancelText: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
  },
  confirmBtn: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm,
    minWidth: 80,
    alignItems: 'center',
  },
  confirmText: {
    color: '#fff',
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
